/**
 * mapHandlers.ts
 * Глобальные обработчики карты.
 **/

import {toLonLat} from "ol/proj";
import type {FeatureLike} from "ol/Feature";
import type OLMap from "ol/Map";
import type {ComputedRef, Ref} from "vue";
import {getArea} from "ol/extent";
import {getCenterPoint, getMinZoomForCircle} from "@/components/modules/MainMap/utils/mapCalculation";
import {isHoveringSquare, isPointInSquare} from "@/components/modules/MainMap/utils/mapCanvas";
import type {DistrictKey} from "@/composables/functions/getDistrictName";
import type {RegionKey} from "@/composables/functions/getRegionName";
import type {FileData, ForecastRegionData} from "@/types/files";

/**
 * Интерфейс параметров добавление обработчиков на клик/ховер
 */
interface MapClickHandlersParams {
    map: OLMap;
    districtDataMap: ComputedRef;
    regionDataMap: ComputedRef;
    selectedDistrict: Ref<DistrictKey | null>;
    selectedRegion: Ref<RegionKey | null>;
}

/**
 * Инициализаций обработчиков клика/ховера
 */
export const MapClickHandlers = (params: MapClickHandlersParams) => {
    const {map, districtDataMap, regionDataMap, selectedDistrict, selectedRegion} = params;

    // Для тестирования. Клик выводит ид региона и координаты в консоль.
    map.on("singleclick", function (event) {
        if (!map) return;

        const coordinateInViewProjection = event.coordinate;
        const coordinateInLonLat = toLonLat(coordinateInViewProjection);

        console.log(`coords:, [${coordinateInLonLat[0].toFixed(4)}, ${coordinateInLonLat[1].toFixed(4)}]`);

        const pixel = event.pixel;

        map.forEachFeatureAtPixel(pixel, (feature) => {
            const regionId = feature.get("id");
            console.log(`"${regionId}": [${coordinateInLonLat[0].toFixed(4)}, ${coordinateInLonLat[1].toFixed(4)}]`);
        });
    });

    // Обработчик клика на квадрат региона/округа
    map.on("singleclick", function (event) {
        if (!map) return;

        const pixel = event.pixel;
        let clickHandled = false;

        // Вычисляем isDistrictView динамически для актуального состояния
        const isDistrictView = selectedDistrict.value === null;

        map.forEachFeatureAtPixel(pixel, (feature) => {
            if (clickHandled) return;

            if (handleFeatureClick(feature, pixel, isDistrictView, map, districtDataMap, regionDataMap, selectedDistrict, selectedRegion)) {
                clickHandled = true;
                return true;
            }
        }, {
            hitTolerance: 5
        });
    });

    // Обработчик hover для замены курсора на квадратах с данными
    map.on("pointermove", (event) => {
        if (!map) return;

        const pixel = event.pixel;
        let foundSquare = false;

        const isDistrictView = selectedDistrict.value === null;

        map.forEachFeatureAtPixel(pixel, (feature) => {
            if (isHoveringSquare(feature, pixel, isDistrictView, map, regionDataMap, districtDataMap)) {
                foundSquare = true;
                return true;
            }
        }, {
            hitTolerance: 5
        });

        map.getTargetElement().style.cursor = foundSquare ? "pointer" : "";
    });
};

/**
 * Обрабатывает клик по квадрату с данными региона/округа
 */
const handleFeatureClick = (
    feature: FeatureLike,
    pixel: number[],
    isDistrictView: boolean,
    map: OLMap,
    districtDataMap: ComputedRef<Map<string, FileData | ForecastRegionData>>,
    regionDataMap: ComputedRef<Map<string, FileData | ForecastRegionData>>,
    selectedDistrict: Ref<DistrictKey | null>,
    selectedRegion: Ref<RegionKey | null>
): boolean => {
    if (!map) return false;

    const featureId = feature.get("id");
    if (!featureId) return false;

    const featureKey = featureId.toLowerCase();

    if (isDistrictView) {
        const districtData = districtDataMap.value.get(featureKey);
        if (!districtData) return false;

        const centerPoint = getCenterPoint(featureKey, null, true);
        if (!centerPoint) return false;

        const centerCoords = centerPoint.getCoordinates();
        const centerPixel = map.getPixelFromCoordinate(centerCoords);

        if (isPointInSquare(pixel, centerPixel)) {
            // Сначала сбрасываем регион, чтобы избежать конфликтов в запросах
            selectedRegion.value = null;
            // Затем устанавливаем новый округ
            selectedDistrict.value = featureId as DistrictKey;
            return true;
        }
    } else {
        const regionData = regionDataMap.value.get(featureKey);
        if (!regionData) return false;

        const geometry = feature.getGeometry();
        if (!geometry) return false;

        const extent = geometry.getExtent();
        const regionArea = getArea(extent);
        const minZoomForCircle = getMinZoomForCircle(regionArea);
        const currentZoom = map.getView().getZoom() || 3;

        if (currentZoom >= minZoomForCircle) {
            const centerPoint = getCenterPoint(featureKey, extent, false);
            if (!centerPoint) return false;

            const centerCoords = centerPoint.getCoordinates();
            const centerPixel = map.getPixelFromCoordinate(centerCoords);

            if (isPointInSquare(pixel, centerPixel)) {
                selectedRegion.value = featureId as RegionKey;
                return true;
            }
        }
    }

    return false;
};